import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/api/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { UserCircle, Key, FloppyDisk, Camera } from '@phosphor-icons/react';

const MyProfile = () => {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState('profile');
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setUsername(user.username || '');
      setEmail(user.email || '');
      setPhotoUrl(user.photo_url || '');
    }
  }, [user]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecciona una imagen');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/users/me/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotoUrl(res.data.photo_url);
      toast.success('Foto actualizada');
      if (refreshUser) await refreshUser();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error subiendo foto');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const saveProfile = async () => {
    if (!name.trim()) {
      toast.error('El nombre no puede estar vacío');
      return;
    }
    setSaving(true);
    try {
      await api.put('/users/me/profile', { name: name.trim(), username: username.trim(), email: email.trim() });
      toast.success('Perfil actualizado');
      if (refreshUser) await refreshUser();
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Error al actualizar perfil';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async () => {
    if (!currentPwd || !newPwd || !confirmPwd) {
      toast.error('Completa todos los campos');
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (newPwd.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    setSaving(true);
    try {
      await api.put('/users/me/password', { current_password: currentPwd, new_password: newPwd });
      toast.success('Contraseña actualizada');
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Error al actualizar contraseña';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] p-4 md:p-8" data-testid="my-profile-page">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 flex items-center gap-3">
          <UserCircle size={32} weight="fill" className="text-[#1D4ED8]" />
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-[var(--text)] tracking-tight">Mi Perfil</h1>
            <p className="text-xs text-[var(--text-muted)] font-medium">Edita cómo apareces en el sistema y tu contraseña.</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[var(--surface)] border border-[var(--border)]">
            <TabsTrigger value="profile" data-testid="tab-profile">
              <UserCircle size={14} weight="bold" className="mr-1" /> Información
            </TabsTrigger>
            <TabsTrigger value="password" data-testid="tab-password">
              <Key size={14} weight="bold" className="mr-1" /> Contraseña
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="pt-4 space-y-3">
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[16px] p-5 space-y-4">
              {/* Profile photo */}
              <div className="flex items-center gap-4 pb-4 border-b border-[var(--border)]">
                {photoUrl ? (
                  <img src={photoUrl} alt="profile" className="w-20 h-20 rounded-full object-cover border-2 border-[var(--primary)]" data-testid="profile-photo" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-[var(--surface-muted)] border-2 border-[var(--border)] flex items-center justify-center text-2xl font-black text-[var(--text-faint)]">
                    {(name || '?')[0].toUpperCase()}
                  </div>
                )}
                <div className="flex-1">
                  <label htmlFor="photo-input" className="inline-flex items-center gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white text-xs font-bold px-3 py-2 rounded-md cursor-pointer disabled:opacity-50" data-testid="upload-photo-btn">
                    <Camera size={14} weight="bold" />
                    {uploadingPhoto ? 'Subiendo…' : 'Cambiar foto'}
                  </label>
                  <input id="photo-input" type="file" accept="image/*" onChange={handlePhotoUpload}
                    disabled={uploadingPhoto} className="hidden" data-testid="photo-file-input" />
                  <p className="text-[10px] text-[var(--text-faint)] mt-1">JPG/PNG, máx 5MB. Solo tú puedes cambiar tu foto.</p>
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)] mb-1 block">Nombre (visible en el dashboard)</label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="Tu nombre" className="bg-[var(--bg)] border-[var(--border)] text-[var(--text)]" data-testid="input-name" />
                <p className="text-[10px] text-[var(--text-faint)] mt-1">Este nombre aparecerá en mensajes, ranking y reportes.</p>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)] mb-1 block">Usuario (alias opcional)</label>
                <Input value={username} onChange={e => setUsername(e.target.value)} placeholder="Tu usuario" className="bg-[var(--bg)] border-[var(--border)] text-[var(--text)]" data-testid="input-username" />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)] mb-1 block">Email</label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" className="bg-[var(--bg)] border-[var(--border)] text-[var(--text)]" data-testid="input-email" />
              </div>
              <Button onClick={saveProfile} disabled={saving} className="bg-[#1D4ED8] hover:bg-[#1E40AF]" data-testid="save-profile-btn">
                <FloppyDisk size={16} weight="bold" className="mr-1" /> {saving ? 'Guardando...' : 'Guardar cambios'}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="password" className="pt-4 space-y-3">
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[16px] p-5 space-y-4">
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)] mb-1 block">Contraseña actual</label>
                <Input type="password" value={currentPwd} onChange={e => setCurrentPwd(e.target.value)} className="bg-[var(--bg)] border-[var(--border)] text-[var(--text)]" data-testid="input-current-pwd" />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)] mb-1 block">Nueva contraseña</label>
                <Input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} placeholder="Mínimo 6 caracteres" className="bg-[var(--bg)] border-[var(--border)] text-[var(--text)]" data-testid="input-new-pwd" />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)] mb-1 block">Confirmar nueva contraseña</label>
                <Input type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} className="bg-[var(--bg)] border-[var(--border)] text-[var(--text)]" data-testid="input-confirm-pwd" />
              </div>
              <Button onClick={savePassword} disabled={saving} className="bg-[#1D4ED8] hover:bg-[#1E40AF]" data-testid="save-password-btn">
                <Key size={16} weight="bold" className="mr-1" /> {saving ? 'Guardando...' : 'Cambiar contraseña'}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MyProfile;
